import { useMemo, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, ChevronRight, Cpu } from 'lucide-react';
import { getProviders, findModel } from '@/lib/providers/registry';
import { useUIStore } from '@/lib/store/uiStore';
import { cn } from '@/lib/utils';

interface Props {
  modelId: string;
  onSelect: (modelId: string) => void;
}

export function ModelMenu({ modelId, onSelect }: Props) {
  // re-subscribe to apiKeys so the provider list updates when keys change
  useUIStore((s) => s.apiKeys);
  // Show only CLI agents (and mock for offline dev). API providers stay in
  // the registry — Settings still needs them for key configuration — but
  // they'd duplicate brands in the picker (Claude CLI + Anthropic API,
  // Gemini CLI + Gemini API, etc.) and Xoro's stated direction is CLI-only.
  const providers = getProviders().filter((p) => p.group !== 'api');
  const current = findModel(modelId);
  const label = current?.model.label ?? 'Pick model';

  // Default-expand only the active provider.
  const initial = useMemo<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    for (const p of providers) out[p.id] = p.id === current?.provider.id;
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.provider.id]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(initial);

  const toggle = (id: string) =>
    setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="focus-ring flex items-center gap-1 rounded-md px-2 py-1 text-[12.5px] text-fg-muted hover:bg-surface-2 hover:text-fg">
          <Cpu className="h-3.5 w-3.5" />
          <span className="font-medium">{label}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 max-h-[420px] w-[260px] overflow-auto rounded-lg border border-border bg-canvas p-1 shadow-menu animate-fade-in"
        >
          <DropdownMenu.Label className="px-2 py-1 text-2xs uppercase tracking-wide text-fg-subtle">
            Model
          </DropdownMenu.Label>

          {providers.map((provider) => {
            const isOpen = !!expanded[provider.id];
            const containsActive = provider.id === current?.provider.id;
            return (
              <DropdownMenu.Group key={provider.id} className="px-0.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle(provider.id);
                  }}
                  className="focus-ring flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-2xs uppercase tracking-wide text-fg-subtle hover:bg-surface-2 hover:text-fg"
                  aria-expanded={isOpen}
                >
                  <ChevronRight
                    className={cn(
                      'h-3 w-3 transition-transform',
                      isOpen && 'rotate-90'
                    )}
                  />
                  <span className="flex-1">{provider.label}</span>
                  {containsActive && (
                    <span className="rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] font-medium text-accent">
                      Active
                    </span>
                  )}
                  {!provider.configured && !containsActive && (
                    <span className="text-[10px] font-medium text-fg-subtle">
                      Not configured
                    </span>
                  )}
                  <span className="ml-1 tabular-nums text-[10px] text-fg-subtle">
                    {provider.models.length}
                  </span>
                </button>

                {isOpen && (
                  <div className="mt-0.5 space-y-px pb-1">
                    {provider.models.map((model) => {
                      const selected = model.id === modelId;
                      const disabled = !provider.configured;
                      return (
                        <DropdownMenu.Item
                          key={model.id}
                          className={cn('menu-item', model.legacy && 'opacity-70')}
                          disabled={disabled}
                          onSelect={() => onSelect(model.id)}
                        >
                          <Check
                            className={cn(
                              'h-3.5 w-3.5 shrink-0',
                              selected ? 'text-accent' : 'opacity-0'
                            )}
                          />
                          <span className="flex-1 truncate">
                            {model.label}
                            {model.legacy && (
                              <span className="ml-1.5 text-2xs text-fg-subtle">Legacy</span>
                            )}
                          </span>
                          {model.badge && (
                            <span className="rounded bg-surface-2 px-1 py-0.5 text-[10px] font-medium text-fg-muted">
                              {model.badge}
                            </span>
                          )}
                        </DropdownMenu.Item>
                      );
                    })}
                  </div>
                )}
              </DropdownMenu.Group>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
